import type { LocationSnapshot, UserLocationState } from "../types/location";

export const LOCATION_RADIUS_OPTIONS = [10, 25, 50, 100] as const;
export const DEFAULT_BROWSING_RADIUS = 25;

export function normalizeLocationSnapshot(input: unknown): LocationSnapshot | null {
  if (!input || typeof input !== "object") return null;
  const source = input as Record<string, unknown>;
  const postalCode = String(source.postalCode || "").trim();
  if (!postalCode) return null;

  return {
    postalCode,
    countryCode: String(source.countryCode || "US").trim().toUpperCase() || "US",
    label: String(source.label || "").trim() || postalCode,
    latitude: Number.isFinite(Number(source.latitude)) ? Number(source.latitude) : null,
    longitude: Number.isFinite(Number(source.longitude)) ? Number(source.longitude) : null,
  };
}

export function getBrowsingRadius(user: UserLocationState | null | undefined): number {
  const value = Number(user?.browsingRadiusMiles);
  if (!Number.isFinite(value)) return DEFAULT_BROWSING_RADIUS;
  return value > 0 ? value : DEFAULT_BROWSING_RADIUS;
}

export function getProfileLocation(user: UserLocationState | null | undefined): LocationSnapshot | null {
  return normalizeLocationSnapshot(user?.profileLocation);
}

export function getBrowsingLocation(user: UserLocationState | null | undefined): LocationSnapshot | null {
  return normalizeLocationSnapshot(user?.browsingLocation) || getProfileLocation(user);
}

export function formatLocationLabel(location: LocationSnapshot | null | undefined, fallback = "Not set"): string {
  return location?.label || fallback;
}

export function hasCoordinates(location: LocationSnapshot | null | undefined): boolean {
  return Number.isFinite(Number(location?.latitude)) && Number.isFinite(Number(location?.longitude));
}

export function haversineMiles(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const radiusMiles = 3958.7613;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radiusMiles * c;
}
